import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ModeleWordGroupe, ReportTemplate, UploadModeleWordResult } from '../models/models';
import api from './api';

/** Clé du cache `printTemplates` : combinaison type_rapport + type_document_code. */
export const printTemplatesKey = (type_rapport?: string | null, type_document_code?: string | null) =>
  `${type_rapport ?? 'all'}:${type_document_code ?? 'all'}`;

interface ReportTemplateState {
  reportTemplateData: ReportTemplate[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedReportTemplate: ReportTemplate | null;
  /** Templates disponibles par contexte d'impression (clé = printTemplatesKey). Séparé de
   *  reportTemplateData pour ne pas écraser la liste de la page de gestion. */
  printTemplates: Record<string, ReportTemplate[]>;
  /** Contextes en cours de chargement (clé = printTemplatesKey). */
  printTemplatesLoading: Record<string, boolean>;
  /** Dictionnaire des balises du modèle Word (guide du formulaire) — chargé une fois. */
  modeleWordVariables: ModeleWordGroupe[];
  /** Envoi d'un modèle Word en cours. */
  modeleWordUploading: boolean;
}

const initialState: ReportTemplateState = {
  reportTemplateData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  selectedReportTemplate: null,
  printTemplates: {},
  printTemplatesLoading: {},
  modeleWordVariables: [],
  modeleWordUploading: false,
};

export const fetchReportTemplateData = createAsyncThunk<
  { data: ReportTemplate[]; total: number; totalPages: number; currentPage: number },
  { type_rapport?: string | null; limit: number; offset: number },
  { rejectValue: string }
>(
  'reportTemplate/fetchAll',
  async ({ type_rapport, limit, offset }, thunkAPI) => {
    try {
      const response = await api.get('/reportTemplate', {
        params: { type_rapport: type_rapport ?? undefined, limit, offset },
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec du chargement.');
    }
  }
);

/**
 * Type_rapport interrogés pour un contexte d'impression, alignés sur la résolution
 * du worker (templateResolver) : IMPRESSION_DOCUMENT retombe sur les templates
 * DOCUMENT_VENTE du même code document. L'ordre fixe la priorité d'affichage.
 */
const PRINT_TYPE_RAPPORT_ALIAS: Record<string, string[]> = {
  IMPRESSION_DOCUMENT: ['IMPRESSION_DOCUMENT', 'DOCUMENT_VENTE'],
};

/**
 * Récupère les templates disponibles pour un contexte d'impression donné
 * (type_rapport + type_document_code). Léger : stocke le résultat dans la map
 * `printTemplates` sans toucher à `reportTemplateData` (page de gestion).
 * Pour IMPRESSION_DOCUMENT, fusionne aussi les templates DOCUMENT_VENTE (alias worker).
 */
export const fetchTemplatesForDocument = createAsyncThunk<
  { key: string; data: ReportTemplate[] },
  { type_rapport: string; type_document_code?: string | null },
  { rejectValue: string }
>(
  'reportTemplate/fetchForDocument',
  async ({ type_rapport, type_document_code }, thunkAPI) => {
    try {
      const types = PRINT_TYPE_RAPPORT_ALIAS[type_rapport] ?? [type_rapport];
      const responses = await Promise.all(types.map((tr) =>
        api.get('/reportTemplate', {
          params: { type_rapport: tr, type_document_code: type_document_code ?? undefined, limit: 100, offset: 0 },
        })
      ));
      // Fusion unique par id en préservant l'ordre de priorité (premier type_rapport d'abord).
      const merged: ReportTemplate[] = [];
      const seen = new Set<number>();
      for (const res of responses) {
        for (const t of (res.data.data ?? []) as ReportTemplate[]) {
          if (t.id != null && !seen.has(t.id)) { seen.add(t.id); merged.push(t); }
        }
      }
      return { key: printTemplatesKey(type_rapport, type_document_code), data: merged };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec du chargement des templates.');
    }
  }
);

export const createOrUpdateReportTemplate = createAsyncThunk<
  { id: number },
  ReportTemplate,
  { rejectValue: string }
>(
  'reportTemplate/createOrUpdate',
  async (template, thunkAPI) => {
    try {
      const response = await api.post('/reportTemplate', template);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la sauvegarde.');
    }
  }
);

export const setDefaultReportTemplate = createAsyncThunk<
  void,
  { id: number; type_rapport?: string | null },
  { rejectValue: string }
>(
  'reportTemplate/setDefault',
  async ({ id, type_rapport }, thunkAPI) => {
    try {
      await api.post('/reportTemplate/setDefault', { id, type_rapport: type_rapport ?? null });
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec.');
    }
  }
);

export const deleteReportTemplate = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>(
  'reportTemplate/delete',
  async (id, thunkAPI) => {
    try {
      await api.delete('/reportTemplate', { params: { id } });
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la suppression.');
    }
  }
);

// ── Modèle Word ─────────────────────────────────────────────────────────────

export const fetchModeleWordVariables = createAsyncThunk<
  ModeleWordGroupe[],
  void,
  { rejectValue: string }
>(
  'reportTemplate/fetchModeleWordVariables',
  async (_, thunkAPI) => {
    try {
      const response = await api.get('/reportTemplate/modeleWord/variables');
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec du chargement des variables.');
    }
  }
);

/**
 * Envoie le modèle Word d'un template (contrôlé côté API avant enregistrement).
 * Ne pose PAS de successMessage : la page recharge la liste sur successMessage,
 * et ce rechargement vide le template sélectionné (formulaire ouvert).
 */
export const uploadModeleWord = createAsyncThunk<
  UploadModeleWordResult & { id: number },
  { id: number; fichier: File },
  { rejectValue: string }
>(
  'reportTemplate/uploadModeleWord',
  async ({ id, fichier }, thunkAPI) => {
    try {
      const form = new FormData();
      form.append('fichier', fichier);
      // Content-Type explicite : sinon l'en-tête JSON par défaut de l'instance
      // fait convertir le FormData en JSON par axios (le navigateur ajoute le boundary).
      const response = await api.post(`/reportTemplate/${id}/modeleWord`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { ...response.data, id };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec de l'envoi du modèle.");
    }
  }
);

const reportTemplateSlice = createSlice({
  name: 'reportTemplate',
  initialState,
  reducers: {
    setSelectedReportTemplate: (state, action: PayloadAction<ReportTemplate | null>) => {
      state.selectedReportTemplate = action.payload;
    },
    clearReportTemplateData: (state) => {
      state.reportTemplateData = [];
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReportTemplateData.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(fetchReportTemplateData.fulfilled,  (state, action) => {
        state.loading = false;
        state.reportTemplateData = action.payload.data;
        state.total       = action.payload.total;
        state.totalPages  = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchReportTemplateData.rejected,   (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(fetchTemplatesForDocument.pending,   (state, action) => {
        state.printTemplatesLoading[printTemplatesKey(action.meta.arg.type_rapport, action.meta.arg.type_document_code)] = true;
      })
      .addCase(fetchTemplatesForDocument.fulfilled, (state, action) => {
        state.printTemplates[action.payload.key] = action.payload.data;
        state.printTemplatesLoading[action.payload.key] = false;
      })
      .addCase(fetchTemplatesForDocument.rejected,  (state, action) => {
        state.printTemplatesLoading[printTemplatesKey(action.meta.arg.type_rapport, action.meta.arg.type_document_code)] = false;
      })

      .addCase(createOrUpdateReportTemplate.pending,   (state) => { state.loading = true; state.error = null; state.successMessage = null; })
      .addCase(createOrUpdateReportTemplate.fulfilled, (state) => { state.loading = false; state.successMessage = 'Template sauvegardé !'; state.printTemplates = {}; })
      .addCase(createOrUpdateReportTemplate.rejected,  (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(setDefaultReportTemplate.pending,   (state) => { state.loading = true; state.error = null; state.successMessage = null; })
      .addCase(setDefaultReportTemplate.fulfilled, (state) => { state.loading = false; state.successMessage = 'Template par défaut mis à jour !'; state.printTemplates = {}; })
      .addCase(setDefaultReportTemplate.rejected,  (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(deleteReportTemplate.pending,   (state) => { state.loading = true; state.error = null; state.successMessage = null; })
      .addCase(deleteReportTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Template supprimé !';
        state.reportTemplateData = state.reportTemplateData.filter(t => t.id !== action.payload);
        state.printTemplates = {};
      })
      .addCase(deleteReportTemplate.rejected,  (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(fetchModeleWordVariables.fulfilled, (state, action) => { state.modeleWordVariables = action.payload; })

      .addCase(uploadModeleWord.pending,   (state) => { state.modeleWordUploading = true; })
      .addCase(uploadModeleWord.fulfilled, (state, action) => {
        state.modeleWordUploading = false;
        // Mise à jour en place de la ligne de la liste (pas de rechargement, cf. thunk)
        const { id, modele_word } = action.payload;
        const ligne = state.reportTemplateData.find(t => t.id === id);
        if (ligne) ligne.options = { ...(ligne.options ?? {}), modele_word };
        state.printTemplates = {};
      })
      .addCase(uploadModeleWord.rejected,  (state) => { state.modeleWordUploading = false; });
  },
});

export const { setSelectedReportTemplate, clearReportTemplateData, clearMessages } = reportTemplateSlice.actions;
export default reportTemplateSlice.reducer;
